import React from 'react'
import { List, useTable, DateField } from '@refinedev/antd'
import { useList, useUpdate } from '@refinedev/core'
import { App, Button, Input, Modal, Space, Table, Tooltip } from 'antd'
import { brand } from '../../theme/brand'
import { ListPage, Pill, StatStrip, TableSurface, type Stat } from '../../theme/listkit'
import { renderAvatarName } from '../../theme/columns'
import { Clock, BadgeCheck, Flag, Ban } from '../../components/icons'
import { outcomeLabel, outcomeTone, areaLabel, countReplies } from './ask'
import { RequestReplies } from './RequestReplies'

export interface BusinessRequest {
  id: string
  category_slug: string
  area: string
  body: string
  status: 'open' | 'closed' | 'removed'
  is_private: boolean
  routed_count: number
  outcome: string | null
  removed_reason: string | null
  created_at: string
  // FK embed — the requester's profile, so the queue never shows a bare uuid.
  requester?: { name: string | null; class_year: string | null } | null
}

/** Requester profile pulled inline via the named profiles FK (no second round trip). */
export const SELECT = '*, requester:profiles!business_requests_requester_profile_fkey(name,class_year)'

const BODY_TRUNCATE = 140
const truncateBody = (s: string) => (s.length > BODY_TRUNCATE ? s.slice(0, BODY_TRUNCATE - 1) + '…' : s)

const REASON_TRUNCATE = 60
const truncateReason = (s: string) => (s.length > REASON_TRUNCATE ? s.slice(0, REASON_TRUNCATE - 1) + '…' : s)

/**
 * Admin Ask the Jaguars monitor (Jaguar-Owned Phase 4, Task K3).
 *
 * List + remove only, same shape as the listings queue and class-year-change
 * queue: there is no admin UPDATE for the normal lifecycle of a request (the
 * member asks, gets routed, replies come in, the member records an outcome) —
 * the row's before-update trigger (backend/supabase/migrations/
 * 20260923120300_jaguar_owned_ask.sql, tg_business_requests_before_update)
 * only ever lets an admin move status to 'removed' with a non-empty
 * removed_reason, and nothing else in the same statement. Removing is
 * therefore the only admin write this page performs.
 *
 * Admins see the requester's name and class year even on a `is_private`
 * request — business_requests_public deliberately keeps that visible to
 * admins (and only admins) so a report or a removal decision isn't made
 * blind. This page is admin-only; never render a private requester's name
 * anywhere a member could see it.
 *
 * Reply counts: business_request_replies has no per-request aggregate column
 * of its own (unlike business_requests.routed_count, which the routing
 * trigger maintains). Rather than fetch the whole table, this scopes the
 * reply query to just the ids on the CURRENT table page (`request_id in
 * (...)`) and to `status = 'visible'` (the same value
 * business_request_replies_public filters on — a 'removed' reply should
 * never inflate the count an admin sees), then rolls the page up with
 * countReplies(). The Replies cell shows "—" instead of a number until that
 * query has real data, so a still-loading or errored fetch can never be
 * misread as "zero replies".
 */
export function AskMonitorPage() {
  const { message } = App.useApp()

  const { tableProps } = useTable<BusinessRequest>({
    resource: 'business_requests',
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    meta: { select: SELECT },
  })

  // Only the current page's request ids — never the whole table.
  const pageIds = React.useMemo(
    () => (tableProps.dataSource ?? []).map((r) => r.id),
    [tableProps.dataSource],
  )
  const { query: repliesQuery } = useList<{ id: string; request_id: string }>({
    resource: 'business_request_replies',
    filters: [
      { field: 'request_id', operator: 'in', value: pageIds },
      { field: 'status', operator: 'eq', value: 'visible' },
    ],
    pagination: { mode: 'off' },
    meta: { select: 'id,request_id' },
    // Disabled (rather than sent with an empty `in (...)`) while the table's
    // own page hasn't resolved yet.
    queryOptions: { enabled: pageIds.length > 0 },
  })
  const repliesReady =
    pageIds.length > 0 && !repliesQuery.isLoading && !repliesQuery.isError && !!repliesQuery.data
  const replyCounts = React.useMemo(
    () => countReplies(repliesQuery.data?.data ?? []),
    [repliesQuery.data],
  )

  // Four bounded pageSize:1 count queries, spelled out (not looped) so the hook
  // call order is statically obvious — same shape as businesses/list.tsx.
  const openQ = useList<BusinessRequest>({
    resource: 'business_requests',
    filters: [{ field: 'status', operator: 'eq', value: 'open' }],
    pagination: { pageSize: 1 },
  })
  const chosenQ = useList<BusinessRequest>({
    resource: 'business_requests',
    // A request an admin later removed shouldn't still count toward "Chose a
    // business" — the outcome is history, but the row itself is gone.
    filters: [
      { field: 'outcome', operator: 'eq', value: 'chose_business' },
      { field: 'status', operator: 'ne', value: 'removed' },
    ],
    pagination: { pageSize: 1 },
  })
  const noMatchQ = useList<BusinessRequest>({
    resource: 'business_requests',
    filters: [
      { field: 'routed_count', operator: 'eq', value: 0 },
      { field: 'status', operator: 'eq', value: 'open' },
    ],
    pagination: { pageSize: 1 },
  })
  const removedQ = useList<BusinessRequest>({
    resource: 'business_requests',
    filters: [{ field: 'status', operator: 'eq', value: 'removed' }],
    pagination: { pageSize: 1 },
  })

  const open = openQ.result?.total ?? 0
  const chosen = chosenQ.result?.total ?? 0
  const noMatch = noMatchQ.result?.total ?? 0
  const removed = removedQ.result?.total ?? 0

  const stats: Stat[] = [
    { label: 'Open requests', value: open.toLocaleString(), icon: Clock, tone: open > 0 ? 'gold' : 'neutral' },
    {
      label: 'Chose a business',
      value: chosen.toLocaleString(),
      icon: BadgeCheck,
      tone: chosen > 0 ? 'green' : 'neutral',
    },
    { label: 'No matches', value: noMatch.toLocaleString(), icon: Flag, tone: noMatch > 0 ? 'red' : 'neutral' },
    { label: 'Removed', value: removed.toLocaleString(), icon: Ban, tone: removed > 0 ? 'jaguar' : 'neutral' },
  ]

  // ── Remove flow ──────────────────────────────────────────────
  const { mutate: update } = useUpdate()
  const [removeTarget, setRemoveTarget] = React.useState<BusinessRequest | null>(null)
  const [reason, setReason] = React.useState('')
  const [removing, setRemoving] = React.useState(false)

  function openRemove(record: BusinessRequest) {
    setRemoveTarget(record)
    setReason('')
  }

  function closeRemove() {
    setRemoveTarget(null)
    setReason('')
  }

  function handleRemove() {
    // In-flight guard: a second OK click (e.g. two fast clicks before the
    // button's own disabled/loading state has painted) must never fire a
    // second update for the same removal.
    if (removing) return
    if (!removeTarget) return
    const trimmed = reason.trim()
    if (!trimmed) return
    setRemoving(true)
    update(
      {
        resource: 'business_requests',
        id: removeTarget.id,
        values: { status: 'removed', removed_reason: trimmed },
        // This page owns its own toasts (message.success/message.error below)
        // — Refine's own notificationProvider would otherwise fire a SECOND,
        // generic toast for the same mutation. Precedent:
        // admin_users/RevokeAdminButton.tsx.
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setRemoving(false)
          message.success('Request removed.')
          closeRemove()
        },
        onError: (e) => {
          setRemoving(false)
          // Refine data-provider errors are plain HttpError objects (a
          // Postgres/Edge Function message + statusCode), not `Error`
          // instances — `e instanceof Error` would silently swallow the
          // database's own reason (e.g. "Admins may only remove a request,
          // with a reason, and change nothing else.") behind a generic
          // fallback.
          message.error((e as { message?: string } | undefined)?.message || 'Failed to remove the request.')
        },
      },
    )
  }

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Jaguar-Owned',
        description:
          'Requests members post in Ask the Jaguars, how they were routed, and how they ended. Admins see names on private requests; members never do. Remove anything that breaks the community guidelines.',
      }}
    >
      {/* Capped — stats AND table — so an ultrawide admin display (3440px)
          doesn't stretch the page across the full viewport width. StatStrip
          is rendered here (not passed to ListPage's own `stats` prop) so it
          sits inside the same cap as the table, matching businesses/list.tsx's
          layout proportions. */}
      <div style={{ maxWidth: 1200 }}>
        <StatStrip stats={stats} columns={4} />
        <List title={false} breadcrumb={false}>
          <TableSurface>
            <Table
              {...tableProps}
              rowKey="id"
              scroll={{ x: true }}
              // Task P4: admins moderate replies on a request (owner replies +
              // member recommendations) right from this monitor, without
              // needing a report first — same ruling as vouches on a listing
              // (docs/JAGUAR-OWNED.md's Moderation section). Every column,
              // the stats strip, and the remove-request flow above are
              // unchanged by this addition.
              expandable={{ expandedRowRender: (record) => <RequestReplies requestId={record.id} /> }}
            >
              <Table.Column<BusinessRequest>
                dataIndex="body"
                title="Request"
                render={(value: string) => (
                  <span style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13, color: brand.ink }}>
                    {truncateBody(value)}
                  </span>
                )}
              />
              <Table.Column<BusinessRequest>
                dataIndex="requester"
                title="Asker"
                render={(_: unknown, record) => (
                  <Space direction="vertical" size={2}>
                    <Space size={6} align="center">
                      {renderAvatarName(record.requester?.name ?? 'Unknown asker')}
                      {record.is_private ? <Pill tone="jaguar">private</Pill> : null}
                    </Space>
                    <span style={{ fontFamily: brand.fSans, fontSize: 11, color: brand.sub, marginLeft: 40 }}>
                      Class of {record.requester?.class_year ?? '—'}
                    </span>
                  </Space>
                )}
              />
              <Table.Column<BusinessRequest>
                dataIndex="category_slug"
                title="Category · area"
                render={(_: unknown, record) => (
                  <span style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 12.5, color: brand.ink }}>
                    {record.category_slug} · {areaLabel(record.area)}
                  </span>
                )}
              />
              <Table.Column<BusinessRequest> dataIndex="routed_count" title="Routed" />
              <Table.Column<BusinessRequest>
                dataIndex="id"
                title="Replies"
                render={(_: unknown, record) => (repliesReady ? replyCounts[record.id] ?? 0 : '—')}
              />
              <Table.Column<BusinessRequest>
                dataIndex="outcome"
                title="Outcome"
                render={(_: unknown, record) => {
                  const pill = (
                    <Pill tone={outcomeTone(record.outcome, record.status)}>
                      {outcomeLabel(record.outcome, record.status)}
                    </Pill>
                  )
                  if (record.status !== 'removed' || !record.removed_reason) return pill
                  return (
                    <Space direction="vertical" size={2}>
                      {pill}
                      <Tooltip title={record.removed_reason}>
                        <span style={{ fontFamily: brand.fSans, fontSize: 11, color: brand.sub, cursor: 'default' }}>
                          {truncateReason(record.removed_reason)}
                        </span>
                      </Tooltip>
                    </Space>
                  )
                }}
              />
              <Table.Column<BusinessRequest>
                dataIndex="created_at"
                title="Age"
                sorter
                render={(value: string) => <DateField value={value} format="MMM D, YYYY" />}
              />
              <Table.Column<BusinessRequest>
                title="Actions"
                dataIndex="actions"
                render={(_: unknown, record) =>
                  record.status !== 'removed' ? (
                    <Button
                      size="small"
                      danger
                      data-testid={`ask-remove-${record.id}`}
                      onClick={() => openRemove(record)}
                    >
                      Remove
                    </Button>
                  ) : null
                }
              />
            </Table>
          </TableSurface>
        </List>
      </div>

      <Modal
        title="Remove this request?"
        open={!!removeTarget}
        onCancel={closeRemove}
        onOk={handleRemove}
        okText="Remove"
        okButtonProps={{ danger: true, disabled: reason.trim() === '' }}
        cancelButtonProps={{ disabled: removing }}
        maskClosable={!removing}
        closable={!removing}
        confirmLoading={removing}
        destroyOnHidden
      >
        <Input.TextArea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this request being removed?"
        />
      </Modal>
    </ListPage>
  )
}
